import { ChangeEvent, FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FieldErrors, UseFormSetValue } from "react-hook-form";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { InputField } from "@/components/InputField/InputField";

import { LiPostType } from "@/enum/liprofile.enum";

import { CreateLiPost } from "../schema/liPost";

interface LeadListSourceSectionProps {
	formValues: Partial<CreateLiPost>;
	setValue: UseFormSetValue<CreateLiPost>;
	errors: FieldErrors<CreateLiPost>;
}

/** "Build the lead list based on" — who to collect (posts/likes/comments) + max per post. */
export const LeadListSourceSection: FunctionComponent<LeadListSourceSectionProps> = ({
	formValues,
	setValue,
	errors,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPost.${key}`);
	const infoRef = useRef<HTMLDivElement>(null);

	return (
		<>
			<div className="text-ssm font-bold mb-1.5">Build the lead list based on</div>
			<div className="text-xs text-text-meta mb-8">
				Only posts from people will be shown and charged for and we dedupliacte results. <br />1
				person posting/liking/commenting 2 posts costs 1 credit.
			</div>
			<div className="flex flex-col gap-4 w-full mb-8">
				{formValues.type && [LiPostType.BY_TOPIC].includes(formValues.type) && (
					<Checkbox
						error={!!errors.posts?.message}
						isChecked={formValues.posts || false}
						name="posts"
						showError={true}
						onChange={() =>
							setValue("posts", !formValues.posts, { shouldDirty: true, shouldValidate: true })
						}
					>
						{formValues.type === LiPostType.BY_TOPIC
							? "Everyone who posted about the topic"
							: "All posts from the added profile"}
					</Checkbox>
				)}
				<Checkbox
					error={!!errors.likesToPost?.message}
					isChecked={formValues.likesToPost || false}
					name="likesToPost"
					showError={true}
					onChange={() =>
						setValue("likesToPost", !formValues.likesToPost, {
							shouldDirty: true,
							shouldValidate: true,
						})
					}
				>
					<>
						{formValues.type === LiPostType.BY_TOPIC && "Everyone who liked such a post"}
						{formValues.type === LiPostType.BY_URL && "Everyone who liked the post"}
						{formValues.type === LiPostType.BY_PROFILE &&
							"Everyone who liked the posts of the added profile"}
					</>
				</Checkbox>
				<Checkbox
					error={!!errors.commentsToPost?.message}
					isChecked={formValues.commentsToPost || false}
					name="commentsToPost"
					showError={true}
					onChange={() =>
						setValue("commentsToPost", !formValues.commentsToPost, {
							shouldDirty: true,
							shouldValidate: true,
						})
					}
				>
					<>
						{formValues.type === LiPostType.BY_TOPIC && "Everyone who commented on such a post"}
						{formValues.type === LiPostType.BY_URL && "Everyone who commented on the added post"}
						{formValues.type === LiPostType.BY_PROFILE &&
							"Everyone who commented on the posts of the added profile"}
					</>
				</Checkbox>
				{/* {formValues.type === LiPostType.BY_PROFILE && formValues.fromMember && (
					<Checkbox
						error={!!errors.likesToComment?.message}
						isChecked={formValues.likesToComment || false}
						name="likesToComment"
						showError={true}
						onChange={() =>
							setValue("likesToComment", !formValues.likesToComment, {
								shouldDirty: true,
								shouldValidate: true,
							})
						}
					>
						Everyone who liked the comment of a profile
					</Checkbox>
				)}
				{formValues.type === LiPostType.BY_PROFILE && formValues.fromMember && (
					<Checkbox
						error={!!errors.commentsToComment?.message}
						isChecked={formValues.commentsToComment || false}
						name="commentsToComment"
						showError={true}
						onChange={() =>
							setValue("commentsToComment", !formValues.commentsToComment, {
								shouldDirty: true,
								shouldValidate: true,
							})
						}
					>
						Everyone who commented on the comment of a profile
					</Checkbox>
				)} */}
				{(formValues.likesToComment || formValues.commentsToComment) && (
					<div className="text-xs text-left items-start gap-1.5 inline-flex flex-col text-text-meta">
						<div
							className="inline-flex flex-row w-[fit-content] text-right group whitespace-nowrap items-center gap-2 bg-tag rounded-md p-1.5 text-tag-dark"
							onMouseEnter={() => {
								const position = infoRef.current?.getBoundingClientRect();

								if (infoRef?.current && position?.right && position.right > window.innerWidth) {
									infoRef.current.style.left = `initial`;
									infoRef.current.style.right = `-40px`;
								}
							}}
						>
							Uses your Linkedin account
						</div>
					</div>
				)}
			</div>

			{(formValues.likesToPost || formValues.commentsToPost) && (
				<div className="flex flex-row w-full">
					<InputField
						error={!!errors.maxPerPost?.message}
						handleChange={(event: ChangeEvent<HTMLInputElement>) => {
							if (!(+event.target.value < 0)) {
								setValue("maxPerPost", +event.target.value, {
									shouldDirty: true,
									shouldValidate: true,
								});
							}
						}}
						label={ts("maxPerPost")}
						name={"maxPerPost"}
						placeholder={ts("maxPerPostPlaceholder")}
						showError={true}
						type="number"
						value={formValues?.maxPerPost || ""}
					/>
				</div>
			)}
		</>
	);
};
